/**
 * DWA Internal dashboard pages — one component per dashboard slug.
 *
 * Architecture (Phase 1):
 *   DWA Internal is structured as Departments → Dashboards. Currently:
 *     Creative   → Creative Scorecard (live)
 *     Web        → SmashBalloon Feed Monitor (coming-soon)
 *     Accounts / Operations / Media → no dashboards yet
 *
 *   Routing is flat aliases (option C). Each dashboard is its own URL:
 *     /dashboard/dwa-creative-scorecard
 *     /dashboard/dwa-web-smashballoon
 *
 *   `DwaInternalDashboardPage` is a thin dispatcher: given a slug, it
 *   looks up the dashboard in the catalogue and renders the appropriate
 *   body. The breadcrumb (Hubs › <home Hub> › <Dashboard>) is drawn by the
 *   shared Hub header (hubs/HubHeader.tsx) that wraps this page.
 *
 * Phase 2 (next session):
 *   - Add dashboard_access to user.client_access[dwa-internal]
 *   - Admin UI checkboxes in Lupa Admin → Users
 *   - Frontend gate: hide dashboards the user can't see in the sidebar
 *     hamburger menu + show 'Not authorized' if they land via direct URL
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ScorecardView } from '../components/ScorecardView';
import { TeamCapacityView } from '../components/TeamCapacityView';
import { BackupMonitorView } from '../components/BackupMonitorView';
import { BlogMonitorView } from '../components/BlogMonitorView';
import { SmashBalloonView } from '../components/SmashBalloonView';
import { BannerManagerView } from '../components/BannerManagerView';
import { ClientBrandsView } from '../components/ClientBrandsView';
import { ClientDatabaseView } from '../components/ClientDatabaseView';
import { useAuth } from '../contexts/AuthContext';
import { HubSpotDashboardView } from '../components/HubSpotDashboardView';
import { findDashboard, DEFAULT_DWA_DASHBOARD_SLUG, HUBSPOT_SECTIONS } from './dwaInternalCatalogue';
import type { DwaDashboard, DwaDepartment } from './dwaInternalCatalogue';
import { canSeeDwaDashboard } from './dwaInternalAccess';

const THEME = {
  label: 'DWA Internal',
  pageBg: '#f8f8f6',
  cardBg: '#ffffff',
  cardBorder: 'rgba(0,0,0,0.08)',
  cardShadow: '0 1px 4px rgba(0,0,0,0.06)',
  accent: '#fec35c',
  highlight: '#0ea5e9',
  sectionTitle: '#1a202c',
  sectionDivider: 'rgba(26,32,44,0.18)',
  textPrimary: '#1a202c',
  textSecondary: '#4a5568',
  textMuted: '#94a3b8',
  rowDivider: 'rgba(0,0,0,0.06)',
  positive: '#15803d',
  negative: '#dc2626',
};

const ComingSoonPlaceholder: React.FC<{ dashboard: DwaDashboard }> = ({ dashboard }) => (
  <div
    style={{
      background: THEME.cardBg,
      border: `1px solid ${THEME.cardBorder}`,
      boxShadow: THEME.cardShadow,
      borderRadius: 4,
      padding: '3rem 2rem',
      textAlign: 'center',
      maxWidth: 640,
      margin: '4rem auto',
    }}
  >
    <p
      style={{
        fontSize: '0.7rem',
        fontWeight: 600,
        letterSpacing: '0.18em',
        textTransform: 'uppercase',
        color: THEME.accent,
        marginBottom: '0.6rem',
      }}
    >
      Coming Soon
    </p>
    <h2
      style={{
        fontSize: '1.4rem',
        color: THEME.textPrimary,
        margin: '0 0 0.75rem',
        fontWeight: 700,
      }}
    >
      {dashboard.name}
    </h2>
    {dashboard.description && (
      <p
        style={{
          fontSize: '0.9rem',
          color: THEME.textSecondary,
          lineHeight: 1.5,
          maxWidth: 480,
          margin: '0 auto',
        }}
      >
        {dashboard.description}
      </p>
    )}
    {dashboard.scope && dashboard.scope.length > 0 && (
      <div style={{ maxWidth: 520, margin: '1.75rem auto 0', textAlign: 'left' }}>
        <p
          style={{
            fontSize: '0.65rem',
            fontWeight: 700,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: THEME.textMuted,
            marginBottom: '0.6rem',
          }}
        >
          Planned scope
        </p>
        <ul
          style={{
            margin: 0,
            paddingLeft: '1.1rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.55rem',
          }}
        >
          {dashboard.scope.map((item) => (
            <li
              key={item}
              style={{ fontSize: '0.82rem', color: THEME.textSecondary, lineHeight: 1.5 }}
            >
              {item}
            </li>
          ))}
        </ul>
      </div>
    )}
  </div>
);

interface PageProps {
  /** Dashboard slug from URL. If omitted, falls back to DEFAULT_DWA_DASHBOARD_SLUG. */
  dashboardSlug?: string;
}

export const DwaInternalDashboardPage: React.FC<PageProps> = ({ dashboardSlug }) => {
  const { user } = useAuth();

  const slug = dashboardSlug || DEFAULT_DWA_DASHBOARD_SLUG;
  const found = findDashboard(slug);

  // Unknown slug — render a not-found card. Don't redirect; the user
  // bookmarked or pasted a URL we don't recognize and silently moving
  // them confuses more than it helps.
  if (!found) {
    return (
      <div
        style={{
          flex: 1,
          background: THEME.pageBg,
          fontFamily: 'Lato, system-ui, sans-serif',
          color: THEME.textPrimary,
          padding: '4rem 2rem',
          textAlign: 'center',
        }}
      >
        <h2>Dashboard not found</h2>
        <p style={{ color: THEME.textSecondary }}>
          The dashboard <code>{slug}</code> doesn't exist or hasn't been built yet.
        </p>
        <p style={{ marginTop: '1rem' }}>
          <Link to={`/dashboard/${DEFAULT_DWA_DASHBOARD_SLUG}`} style={{ color: THEME.textPrimary }}>
            Go to the Creative Scorecard
          </Link>
        </p>
      </div>
    );
  }

  const { dashboard } = found;

  // Per-user dashboard access gate. Frontend mirror of backend's
  // assert_can_see_dwa_dashboard — the backend remains the security
  // boundary; this just keeps the UX from rendering content the user
  // can't actually consume.
  if (!canSeeDwaDashboard(user ?? null, dashboard.slug)) {
    return (
      <div
        style={{
          flex: 1,
          background: THEME.pageBg,
          fontFamily: 'Lato, system-ui, sans-serif',
          color: THEME.textPrimary,
          padding: '4rem 2rem',
          textAlign: 'center',
        }}
      >
        <h2 style={{ marginBottom: '0.75rem' }}>Not authorized</h2>
        <p style={{ color: THEME.textSecondary }}>
          Your account doesn't have access to <strong>{dashboard.name}</strong>.
        </p>
        <p style={{ color: THEME.textMuted, fontSize: '0.85rem', marginTop: '0.5rem' }}>
          Contact a DWA administrator if you think this is a mistake.
        </p>
        <p style={{ marginTop: '1.5rem' }}>
          <Link to="/" style={{ color: THEME.textPrimary }}>
            ← Back to Hubs
          </Link>
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        flex: 1,
        background: THEME.pageBg,
        fontFamily: 'Lato, system-ui, sans-serif',
        color: THEME.textPrimary,
      }}
    >
      <main style={{ padding: 'clamp(1rem, 3vw, 1.5rem)', maxWidth: '1440px', margin: '0 auto' }}>
        {dashboard.status === 'live' && dashboard.slug === 'dwa-creative-scorecard' && (
          <ScorecardView theme={THEME} />
        )}
        {dashboard.status === 'live' && dashboard.slug === 'dwa-operations-team-capacity' && (
          <TeamCapacityView theme={THEME} />
        )}
        {dashboard.status === 'live' && dashboard.slug === 'dwa-web-backup-monitor' && (
          <BackupMonitorView theme={THEME} />
        )}
        {dashboard.status === 'live' && dashboard.slug === 'dwa-web-blog-monitor' && (
          <BlogMonitorView theme={THEME} />
        )}
        {dashboard.status === 'live' && dashboard.slug === 'dwa-web-smashballoon' && (
          <SmashBalloonView theme={THEME} />
        )}
        {dashboard.status === 'live' && dashboard.slug === 'dwa-web-banners' && (
          <BannerManagerView theme={THEME} />
        )}
        {dashboard.status === 'live' && dashboard.slug === 'dwa-creative-client-brands' && (
          <ClientBrandsView theme={THEME} />
        )}
        {dashboard.status === 'live' && dashboard.slug === 'dwa-accounts-client-database' && (
          <ClientDatabaseView theme={THEME} />
        )}
        {dashboard.status === 'live' && dashboard.slug in HUBSPOT_SECTIONS && (
          <HubSpotDashboardView
            theme={THEME}
            section={HUBSPOT_SECTIONS[dashboard.slug as keyof typeof HUBSPOT_SECTIONS]}
          />
        )}
        {dashboard.status === 'coming-soon' && (
          <ComingSoonPlaceholder dashboard={dashboard} />
        )}
      </main>
    </div>
  );
};

export default DwaInternalDashboardPage;

// Re-export the catalogue types so consumers (sidebar, admin UI) can
// import from one place when they need to render the full list.
export type { DwaDashboard, DwaDepartment };
